/**
 * Agent-Chat Main Application
 * World-class AI Agent Orchestration Platform with Routing
 */

import React from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { I18nextProvider } from 'react-i18next'
import { Toaster } from 'react-hot-toast'
import i18n from './app/i18n'
import { ThemeProvider } from './providers/ThemeProvider'
import { VariantLayout } from './components/shared/VariantLayout'
import { ChatUIVariantProvider } from './shared/ui-variant/ChatUIVariantProvider'
import { AUTH_CALLBACK_PATH, AuthProvider, Authenticated, Unauthenticated, AuthLoading, useAuth } from '@agion/auth'
import type { AuthConfig } from '@agion/auth'
import { AuthCallback } from './components/auth/AuthCallback'
import FileManagerPage from './pages/FileManagerPage'
import ChatLandingPage from './pages/ChatLandingPage'

import ProfilePage from './pages/ProfilePage'
import MissionChatPage from './pages/MissionChatPage'
import UnauthorizedPage from './pages/UnauthorizedPage'
import { AppInitializer } from './components/AppInitializer'
import { config } from './utils/config'
import { LoadingSpinner } from './components/shared/LoadingSpinner'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      retry: (failureCount, error) => {
        if (error instanceof Error && error.message.includes('4')) return false
        return failureCount < 3
      },
    },
  },
})

const authConfig: AuthConfig = {
  authUrl: config.authServiceUrl,
  apiUrl: config.apiUrl,
  loginUrl: config.loginUrl,
  appName: config.oidcClientId || 'chat-ui',
  hasAuthCallbackRoute: true,
}

function App() {
  return (
    <ThemeProvider>
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider config={authConfig}>
        <BrowserRouter>
        <Routes>
          {/* Auth callback — public, outside auth gate */}
          <Route path={AUTH_CALLBACK_PATH} element={<AuthCallback />} />
          {/* Reached signed in or out: a signed-out visitor resolves no variant
              and gets the current page; a resolved next member gets the new one
              without the chat rail (PLAN-20260929 slice 8). */}
          <Route path="/unauthorized" element={<ChatUIVariantProvider><VariantLayout frame="bare"><UnauthorizedPage /></VariantLayout></ChatUIVariantProvider>} />

          {/* Protected routes. The UI variant (PLAN-20260929) resolves only here;
              the public routes above always render the current chat. */}
          <Route element={<AuthGate />}>
            <Route element={<AppInitializer><ChatUIVariantProvider><Outlet /></ChatUIVariantProvider></AppInitializer>}>
              <Route path="/chat" element={<VariantLayout><ChatLandingPage /></VariantLayout>} />
              <Route path="/chat/:sessionId" element={<Navigate to="/chat" replace />} />
              <Route path="/files" element={<VariantLayout><FileManagerPage /></VariantLayout>} />
              <Route path="/profile" element={<VariantLayout><ProfilePage /></VariantLayout>} />
              <Route path="/mission/:missionId" element={<VariantLayout><MissionChatPage /></VariantLayout>} />
              <Route path="*" element={<Navigate to="/chat" replace />} />
            </Route>
          </Route>

          <Route path="/" element={<Navigate to="/chat" replace />} />
        </Routes>

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            className: 'toast-notification',
            success: { className: 'toast-notification toast-success' },
            error: { className: 'toast-notification toast-error' },
          }}
        />
      </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
    </I18nextProvider>
    </ThemeProvider>
  )
}

/** Replaces AuthGuard — shows loading, redirects unauthenticated, renders children */
function AuthGate() {
  return (
    <>
      <AuthLoading>
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="large" />
        </div>
      </AuthLoading>
      <Authenticated>
        <Outlet />
      </Authenticated>
      <Unauthenticated>
        <SSORedirect />
      </Unauthenticated>
    </>
  )
}

/** Redirects to SSO login when unauthenticated */
function SSORedirect() {
  const { client } = useAuth()

  React.useEffect(() => {
    if (!client) return
    const returnUrl = window.location.pathname + window.location.search
    client.initiateSSO(returnUrl).catch(console.error)
  }, [client])

  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner size="large" />
    </div>
  )
}

export default App
